import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useProfileDetails } from "../lib/react-query/profile";
import { useProjectsList } from "../lib/react-query/projects";

const CURRENT_PROJECT_PAGE_SIZE = 100;

/**
 * Shared hook that reads client-mode eligibility from profile details and uses
 * a single normal projects query to derive the current accessible project.
 *
 * Backend is source of truth for client-only role classification via
 * `has_only_client_roles`. Frontend no longer crawls paginated project data
 * just to derive a boolean.
 */
export const useClientProjectContext = () => {
  const user = useSelector((state) => state?.user?.user);
  const userId = user?.id;
  const hasToken = !!user?.token?.access;
  const profileQuery = useProfileDetails(hasToken && !!userId);
  const {
    data: profileDetails,
    isLoading: profileLoading,
    error: profileError,
  } = profileQuery;

  const {
    data: projectsData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjectsList(
    { ordering: "-updated_at", page_size: CURRENT_PROJECT_PAGE_SIZE },
    hasToken && !!userId,
  );

  const allProjects = useMemo(
    () => projectsData?.items || projectsData?.results || [],
    [projectsData],
  );
  const isClientByRole = profileDetails?.has_only_client_roles === true;

  const isClientModeActive = useMemo(() => {
    if (!isClientByRole) return false;
    return profileDetails?.client_mode_enabled !== false;
  }, [isClientByRole, profileDetails?.client_mode_enabled]);

  const currentProject = useMemo(() => {
    if (allProjects.length === 0) return null;

    const accessible = allProjects.filter(
      (project) => project.user_membership_status !== "PENDING",
    );
    if (accessible.length === 0) return null;

    const active = accessible.find((project) => project.status === "ACTIVE");
    if (active) return active;

    return accessible[0];
  }, [allProjects]);

  return {
    isClientByRole,
    isClientModeActive,
    isClient: isClientModeActive,
    currentProject,
    isLoading: profileLoading || projectsLoading,
    error: profileError || projectsError,
    profileDetails,
    profileLoading,
    profileError,
  };
};

/** Convenience wrapper for presentational components. */
export const useIsClient = () => {
  const {
    isClient: isClientModeActive,
    isClientByRole,
    isLoading,
    profileDetails,
  } = useClientProjectContext();
  return {
    isClient: isClientModeActive,
    isClientModeActive,
    isClientByRole,
    profileDetails,
    isLoading,
  };
};

/** Convenience wrapper for presentational components. */
export const useCurrentProject = () => {
  const { currentProject, isLoading, profileDetails } = useClientProjectContext();
  return { currentProject, isLoading, profileDetails };
};
